<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="tw-mb-[24px]">
    <div
      v-if="!hideHeading"
      class="tw-my-[24px] tw-flex tw-items-center tw-justify-between"
    >
      <span
        class="tw-text-[16px] tw-font-semibold tw-leading-[21px] tw-text-primary-grey"
      >
        Property type
      </span>
      <button
        type="button"
        class="tw-rounded-[4px] tw-p-1 tw-text-[14px] tw-font-semibold tw-leading-[21px] tw-text-uhblue-500 tw-underline tw-decoration-uhblue-500 tw-decoration-solid hover:tw-bg-[#F3F4F6] focus-visible:tw-ring focus-visible:tw-ring-sky-200 focus-visible:tw-ring-opacity-50 disabled:tw-bg-white disabled:tw-text-gray-400 disabled:tw-decoration-gray-400"
        aria-label="Clear property type filters"
        :disabled="isClearDisabled"
        @click.prevent="clearValues"
      >
        Clear
      </button>
    </div>
    <div
      class="tw-flex tw-items-start tw-gap-[16px]"
      :class="
        visiblePropertyTypesCount === 4
          ? 'tw-flex-row tw-flex-wrap'
          : 'tw-flex-row'
      "
    >
      <button
        v-for="propertyType in propertyTypes"
        :key="propertyType.id"
        type="button"
        :aria-pressed.attr="isActiveType(propertyType)"
        :aria-disabled="
          isLastActiveType(propertyType) ? 'true' : 'false'
        "
        :data-testid="'property-type-' + propertyType.id"
        :disabled="isLastActiveType(propertyType)"
        :class="[
          'tw-flex',
          'tw-content-center',
          'tw-items-center',
          'tw-border',
          'tw-m-0',
          compact ? 'tw-border-transparent' : 'tw-border-gray-300',
          'tw-bg-white',
          isLastActiveType(propertyType)
            ? 'tw-cursor-not-allowed tw-opacity-80'
            : 'hover:tw-cursor-pointer',
          'focus-visible:tw-ring',
          'focus-visible:tw-ring-offset-1',
          'focus-visible:tw-ring-sky-400',
          'focus-visible:tw-ring-opacity-50',
          compact
            ? 'focus-visible:tw-border-transparent'
            : enableDarkBlueAccent
            ? 'focus-visible:tw-border-[#0082B8]'
            : 'focus-visible:tw-border-uhblue-700',
          compact ? 'tw-rounded-full' : 'tw-rounded-[24px]',
          compact ? 'tw-justify-center' : null,
          propertyType.visible ? null : 'tw-hidden',
          compact || isActiveType(propertyType)
            ? null
            : enableDarkBlueAccent
            ? 'hover:tw-border-[#0082B8]'
            : 'hover:tw-border-uhblue-700',
          isActiveType(propertyType) && !compact ? ['tw-border-white'] : null,
          visiblePropertyTypesCount === 4
            ? 'tw-w-[calc(50%_-_8px)] tw-flex-none'
            : 'tw-w-full tw-flex-1',
        ]"
        @click="toggleType(propertyType)"
      >
        <div
          :class="[
            'tw-flex',
            'tw-items-center',
            'tw-justify-center',
            'tw-border',
            'tw-h-[100%]',
            'tw-w-[100%]',
            compact ? 'tw-gap-2' : 'tw-gap-1',
            compact ? 'tw-rounded-full' : 'tw-rounded-[24px]',
            compact ? 'tw-flex-row' : 'tw-flex-col',
            compact ? 'tw-p-2.5' : 'tw-py-4',
            isActiveType(propertyType)
              ? [
                  enableDarkBlueAccent
                    ? 'tw-border-[#0082B8]'
                    : 'tw-border-uhblue-500',
                  enableDarkBlueAccent
                    ? 'hover:tw-border-[#0082B8]'
                    : 'hover:tw-border-uhblue-700',
                  enableDarkBlueAccent
                    ? 'tw-shadow-[inset_0_0_0_1px_#0082B8]'
                    : 'tw-shadow-[inset_0_0_0_1px_#00A2E1]',
                ]
              : [
                  compact ? 'tw-border-gray-300' : 'tw-border-white',
                  compact
                    ? enableDarkBlueAccent
                      ? 'hover:tw-border-[#0082B8]'
                      : 'hover:tw-border-uhblue-700'
                    : null,
                  'tw-rounded-[24px]',
                ],
          ]"
        >
          <div
            :class="[
              'tw-flex tw-h-[24px] tw-w-[24px] tw-items-center tw-justify-center',
              enableDarkBlueAccent && isActiveType(propertyType)
                ? 'tw-text-[#0082B8]'
                : null,
            ]"
            v-html="propertyType.icon"
          ></div>

          <div
            class="tw-text-center tw-text-[14px] tw-font-normal tw-leading-[21px] tw-text-[#3B4143]"
          >
            {{ propertyType.name }}
          </div>

          <div
            v-if="showCount"
            class="tw-text-center tw-text-[14px] tw-font-normal tw-text-gray-500"
          >
            ({{ propertyType.count ?? 0 }})
          </div>
        </div>
      </button>
    </div>
  </div>
</template>

<script>
import {
  normalizePropertyTypeValue,
  propertyTypeSelectionToValue,
  togglePropertyTypeSelection,
} from './helpers.ts';
import { PROPERTY_TYPES } from './constants.ts';
import { useSearchModeStore } from '@js/stores/search/searchModeStore';

export default {
  name: 'StackedPropertyTypeFilter',
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
    showCount: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    active: {
      type: [String, Array],
      default: () => null,
    },
    hideHeading: {
      type: Boolean,
      default: false,
    },
    propertyTypes: {
      type: Array,
      default: () => PROPERTY_TYPES,
    },
    enableDarkBlueAccent: {
      type: Boolean,
      default: false,
    },
  },
  data: () => {
    return {
      activeTypes: [],
      syncingFromProp: false,
    };
  },
  computed: {
    blendedSearch() {
      return useSearchModeStore().blendedSearch;
    },
    isClearDisabled() {
      return this.activeTypes.length === 0;
    },
    visiblePropertyTypesCount() {
      return this.propertyTypes.filter((type) => type.visible).length;
    },
  },
  watch: {
    active: {
      deep: true,
      handler() {
        this.syncActiveTypesFromProp();
      },
    },
    activeTypes: {
      deep: true,
      handler() {
        if (!this.syncingFromProp) {
          this.updateState();
        }
      },
    },
  },
  mounted() {
    this.syncActiveTypesFromProp();
  },
  methods: {
    clearValues() {
      this.activeTypes = [];
    },
    isActiveType(type) {
      return this.activeTypes.findIndex((t) => t === type.id) !== -1;
    },
    isLastActiveType(type) {
      return this.activeTypes.length === 1 && this.activeTypes[0] === type.id;
    },
    toggleType(type) {
      if (this.isLastActiveType(type)) {
        return;
      }

      this.activeTypes = togglePropertyTypeSelection(
        this.activeTypes,
        type.id,
        this.blendedSearch
      );
    },
    activeTypeIdsEqual(nextActiveTypes) {
      if (this.activeTypes.length !== nextActiveTypes.length) {
        return false;
      }

      return this.activeTypes.every(
        (type, index) => type === nextActiveTypes[index]
      );
    },
    syncActiveTypesFromProp() {
      const activeIds = normalizePropertyTypeValue(this.active).filter((id) =>
        this.propertyTypes.some((type) => type.id === id)
      );

      if (this.activeTypeIdsEqual(activeIds)) {
        return;
      }

      this.syncingFromProp = true;
      this.activeTypes = activeIds;
      this.syncingFromProp = false;
    },
    updateState() {
      const value = propertyTypeSelectionToValue(
        this.activeTypes,
        this.multiple
      );

      // Send to GA
      this.sendGAEvent(
        'Properties',
        'PropertyTypeFilterChange',
        Array.isArray(value) ? value.join(',') : value
      );

      this.$emit('change', value);
    },
    sendGAEvent(type, action, value) {
      if (typeof ga === 'undefined') {
        return;
      }

      ga('send', 'event', type, action, value);
    },
  },
};
</script>
